import { useState, useEffect } from 'react';
import bellNotificationsConfig from '../constants/bellNotifications.json';
import apiClient from '../lib/api/apiClient';

interface BellNotification {
  id: number;
  user_id: number;
  type: keyof typeof bellNotificationsConfig;
  replacements: string[];
  created_at: string;
  link_id: number;
  is_read: boolean;
}

interface ProcessedNotification extends BellNotification {
  processedMessage: string;
  linkUrl: string | null;
}

export const useBellNotifications = () => {
  const [notifications, setNotifications] = useState<ProcessedNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const processNotification = (notification: BellNotification): ProcessedNotification => {

    // TODO see if we really need to add this configuration check.
    //  const config = bellNotificationsConfig[notification.type];

    // if (!config) {
    //   console.warn(`Unknown notification type: ${notification.type}`);
    //   return {
    //     ...notification,
    //     processedMessage: 'Unknown notification type',
    //     linkUrl: null
    //   };
    // }

    // Process the message template with dynamic data
    const config = notification.notificationTemplate;
    if (!notification.notificationTemplate) {
      console.log(notification)
      // debugger;
      return {
        notification: null,
        processedMessage: 'Unknown notification type',
        linkUrl: null
      };
    }
    let processedMessage = notification.notificationTemplate.message;


    const fillTemplate = (template, replacements) => {

      return template.replace(/{{(.*?)}}/g, (match, p1) => replacements[p1] || match);
    }
    processedMessage = fillTemplate(config.message, notification.replacements);

    // Determine the final link URL
    let linkUrl: string | null = null;
    const linkTarget = (config as any).link_to || (config as any).link;
    if (linkTarget && linkTarget !== false) {
      linkUrl = linkTarget;
      // If idNeeded is true, we might need to append the link_id or notification id
      if (config.idNeeded && notification.link_id) {
        linkUrl = `${linkTarget}?deal=${notification.link_id}`;
      }
    }

    return {
      ...notification,
      processedMessage,
      linkUrl
    };
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.get('/user/bell-notifications');
      const data: BellNotification[] = response.data;


      // Process each notification
      const processedNotifications = data.map(processNotification);

      setNotifications(processedNotifications);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch notifications');
      console.error('Error fetching bell notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId: number) => {
    try {
      await apiClient.patch(`/user/bell-notifications/${notificationId}/read`);

      // Update local state
      setNotifications(prev =>
        prev.map(notification =>
          notification.id === notificationId
            ? { ...notification, is_read: true }
            : notification
        )
      );
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await apiClient.patch('/user/bell-notifications/read-all');

      // Update local state
      setNotifications(prev =>
        prev.map(notification => ({ ...notification, is_read: true }))
      );
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  };

  const deleteNotification = async (notificationId: number) => {
    try {
      await apiClient.delete(`/user/bell-notifications/${notificationId}`);

      // Remove from local state
      setNotifications(prev => prev.filter(notification => notification.id !== notificationId));
    } catch (err) {
      console.error('Error deleting notification:', err);
      throw err;
    }
  };

  const deleteNotifications = async (notificationIds: number[]) => {
    try {
      await apiClient.post('/user/bell-notifications/delete-bulk', { ids: notificationIds });

      // Remove from local state
      setNotifications(prev => prev.filter(notification => !notificationIds.includes(notification.id)));
    } catch (err) {
      console.error('Error deleting notifications:', err);
      throw err;
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  useEffect(() => {
    fetchNotifications();
  }, []);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteNotifications,
    refetch: fetchNotifications
  };
};

export default useBellNotifications;
